import { useMemo, useState } from 'react';
import type { Customer, Lang } from '../data/types';
import { useDashboard } from '../data/context';
import { t } from '../i18n/t';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import { Tags } from '../components/Tags';

type Segment = 'all' | 'regular' | 'vip';

interface CustomersViewProps {
  lang: Lang;
  onOpen: (c: Customer) => void;
}

const GRID_STYLE = { gridTemplateColumns: '2.2fr 0.7fr 0.8fr 1.3fr 52px' };

export function CustomersView({ lang, onOpen }: CustomersViewProps) {
  const { customers: allCustomers } = useDashboard();
  const [seg, setSeg] = useState<Segment>('all');
  const [q, setQ] = useState('');

  const counts = {
    all: allCustomers.length,
    regular: allCustomers.filter((c) => c.tags.includes('regular')).length,
    vip: allCustomers.filter((c) => c.tags.includes('vip')).length,
  };

  const segs: { id: Segment; labelKey: string; n: number }[] = [
    { id: 'all', labelKey: 'all', n: counts.all },
    { id: 'regular', labelKey: 'tag_regular', n: counts.regular },
    { id: 'vip', labelKey: 'tag_vip', n: counts.vip },
  ];

  const rows = useMemo(() => {
    let list = [...allCustomers].sort((a, b) => b.visits - a.visits);
    if (seg === 'vip') list = list.filter((c) => c.tags.includes('vip'));
    if (seg === 'regular') list = list.filter((c) => c.tags.includes('regular'));
    const k = q.trim().toLowerCase();
    if (k) {
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(k) ||
          (c.phone || '').includes(k) ||
          (c.email || '').toLowerCase().includes(k),
      );
    }
    return list;
  }, [seg, q, allCustomers]);

  return (
    <div className="view">
      <div className="view-pad">
        <div className="filterbar">
          {segs.map((s) => (
            <button
              key={s.id}
              type="button"
              className={'fchip' + (seg === s.id ? ' on' : '')}
              onClick={() => setSeg(s.id)}
            >
              {t(s.labelKey, lang)}
              <span className="n">{s.n}</span>
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <div className="search" style={{ minWidth: 220 }}>
            <Icon name="search" size={15} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('search', lang)}
              aria-label={t('search', lang)}
            />
          </div>
        </div>

        <div className="tablewrap">
          <div className="thead" style={GRID_STYLE} role="row">
            <span>{t('name', lang)}</span>
            <span>{t('visits', lang)}</span>
            <span>{t('avg', lang)}</span>
            <span>{t('lastVisit', lang)}</span>
            <span />
          </div>
          {rows.map((c) => (
            <div
              key={c.id}
              className="trow"
              style={GRID_STYLE}
              onClick={() => onOpen(c)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(c);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={c.name}
            >
              <span className="cell-name">
                <Avatar name={c.name} size="sm" />
                <span className="nm">{c.name}</span>
                <Tags tags={c.tags} lang={lang} max={1} />
              </span>
              <span className="cell-num">{c.visits}</span>
              <span className="cell-num">{c.spend}</span>
              <span className="cell-mute tnum">{c.last}</span>
              <span className="cell-mute" style={{ textAlign: 'right' }}>
                <Icon name="cright" size={16} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
